import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteRow, deleteRows, insertRow, listRows, updateRow, updateRows } from "@/lib/adminApi";
import {
  MEDIA_STATUS,
  MEDIA_TYPE,
  formatSize,
  metaFor,
  optionsFrom,
  type MediaAsset,
} from "@/lib/adminContent";
import {
  isStoragePath,
  resolveAssetUrls,
  uploadMediaFile,
} from "@/lib/mediaStorage";
import { supabase } from "@/lib/supabase";
import {
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnAccent,
  btnGhost,
  btnPrimary,
  fieldInput,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

interface UploadItem {
  id: string;
  name: string;
  status: "uploading" | "done" | "error";
  message?: string;
}

export default function AdminMediaLibrary() {
  const [rows, setRows] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [folderFilter, setFolderFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [uploadFolder, setUploadFolder] = useState("Uploads");
  const [dragActive, setDragActive] = useState(false);
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setSelectedIds([]);
    try {
      setRows(await listRows<MediaAsset>("media_assets", "created_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load the media library.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const storagePaths = useMemo(
    () => rows.filter((row) => isStoragePath(row.url)).map((row) => row.url as string),
    [rows],
  );
  const pathKey = useMemo(() => storagePaths.slice().sort().join("|"), [storagePaths]);

  useEffect(() => {
    let active = true;
    if (!pathKey) {
      setSignedUrls({});
      return () => { active = false; };
    }
    resolveAssetUrls(pathKey.split("|")).then((map) => {
      if (active) setSignedUrls(map);
    });
    return () => { active = false; };
  }, [pathKey]);

  const displayUrl = useCallback(
    (url: string | null): string | null => {
      if (!url) return null;
      return isStoragePath(url) ? signedUrls[url] || null : url;
    },
    [signedUrls],
  );

  const update = async (id: number, patch: Partial<MediaAsset>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("media_assets", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const remove = async (id: number) => {
    const current = rows.find((row) => row.id === id);
    setRows((list) => list.filter((row) => row.id !== id));
    setSelectedId(null);
    setSelectedIds((list) => list.filter((value) => value !== id));
    try {
      await deleteRow("media_assets", id);
      if (current && isStoragePath(current.url)) {
        await supabase.storage.from("public").remove([current.url as string]);
      }
      notify(`${current?.file_name || "Asset"} deleted.`);
    } catch {
      setError("We couldn't delete that asset. Please try again.");
      load();
    }
  };

  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const batch = files.map((file, index) => ({
      id: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      status: "uploading" as const,
    }));
    setUploads((list) => [...batch, ...list]);

    const { data } = await supabase.auth.getUser();
    const uploadedBy = data.user?.email || "Admin";

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index];
      const batchId = batch[index].id;
      try {
        const result = await uploadMediaFile(file);
        const created = await insertRow<MediaAsset>("media_assets", {
          file_name: result.fileName,
          title: file.name.replace(/\.[^.]+$/, ""),
          asset_type: result.assetType,
          folder: uploadFolder,
          url: result.path,
          size_kb: result.sizeKb,
          dimensions: result.dimensions,
          alt_text: file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
          uploaded_by: uploadedBy,
          usage_count: 0,
          status: "active",
        });
        setRows((list) => [created, ...list]);
        setUploads((list) => list.map((item) => (item.id === batchId ? { ...item, status: "done" } : item)));
      } catch (err) {
        const message = err instanceof Error && err.message ? err.message : "Upload failed.";
        setUploads((list) =>
          list.map((item) => (item.id === batchId ? { ...item, status: "error", message } : item)),
        );
      }
    }

    notify(`${files.length} file${files.length === 1 ? "" : "s"} processed.`);
    window.setTimeout(() => {
      setUploads((list) => list.filter((item) => item.status === "uploading"));
    }, 4000);
  };

  const folders = useMemo(() => Array.from(new Set(rows.map((row) => row.folder || "Unfiled"))).sort(), [rows]);

  const folderOptions = useMemo(() => {
    const set = new Set<string>(["Uploads", ...folders]);
    return Array.from(set).map((folder) => ({ value: folder, label: folder }));
  }, [folders]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== "all" && row.asset_type !== typeFilter) return false;
      if (folderFilter !== "all" && (row.folder || "Unfiled") !== folderFilter) return false;
      if (!query) return true;
      return [row.file_name, row.title || "", row.alt_text || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, typeFilter, folderFilter]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allSelected = filtered.length > 0 && filtered.every((row) => selectedSet.has(row.id));

  const toggleSelect = (id: number) => {
    setConfirmBulk(false);
    setSelectedIds((list) => (list.includes(id) ? list.filter((value) => value !== id) : [...list, id]));
  };

  const toggleAll = () => {
    setConfirmBulk(false);
    if (allSelected) {
      setSelectedIds((list) => list.filter((id) => !filtered.some((row) => row.id === id)));
    } else {
      setSelectedIds((list) => Array.from(new Set([...list, ...filtered.map((row) => row.id)])));
    }
  };

  const bulkSetStatus = async (status: string, message: string) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    setRows((list) => list.map((row) => (ids.includes(row.id) ? { ...row, status } : row)));
    try {
      await updateRows("media_assets", ids, { status });
      notify(message);
      setSelectedIds([]);
    } catch {
      setError("We couldn't update those assets. Please try again.");
      load();
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setBulkBusy(true);
    const doomed = rows.filter((row) => ids.includes(row.id));
    setRows((list) => list.filter((row) => !ids.includes(row.id)));
    try {
      await deleteRows("media_assets", ids);
      const paths = doomed.filter((row) => isStoragePath(row.url)).map((row) => row.url as string);
      if (paths.length) await supabase.storage.from("public").remove(paths);
      notify(`${ids.length} asset${ids.length === 1 ? "" : "s"} deleted.`);
      setSelectedIds([]);
    } catch {
      setError("We couldn't delete those assets. Please try again.");
      load();
    } finally {
      setBulkBusy(false);
      setConfirmBulk(false);
    }
  };

  const selected = rows.find((row) => row.id === selectedId) || null;
  const totalSize = rows.reduce((sum, row) => sum + Number(row.size_kb || 0), 0);
  const activeUploads = uploads.filter((item) => item.status === "uploading").length;

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="Media Library"
        subtitle="Every photo, video and document in one place — drag files in to upload, then search, folder and attach them anywhere."
      />

      <StatCards
        stats={[
          { label: "Total assets", value: rows.length, icon: "ri-image-2-line", tone: "primary" },
          { label: "Images", value: rows.filter((row) => row.asset_type === "image").length, icon: "ri-image-line", tone: "primary" },
          { label: "Documents & video", value: rows.filter((row) => row.asset_type !== "image").length, icon: "ri-file-line", tone: "accent" },
          { label: "Total size", value: formatSize(totalSize), icon: "ri-hard-drive-2-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <div
          onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(event) => { event.preventDefault(); setDragActive(false); handleFiles(event.dataTransfer.files); }}
          className={`bg-background-50 border-2 border-dashed rounded-lg p-6 md:p-8 transition-colors ${
            dragActive ? "border-primary-500 bg-primary-100/40" : "border-background-300"
          }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-5">
            <span className={`w-14 h-14 flex items-center justify-center rounded-full shrink-0 ${dragActive ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"}`}>
              <i className={`${activeUploads > 0 ? "ri-loader-4-line animate-spin" : "ri-upload-cloud-2-line"} text-2xl`}></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg font-semibold text-foreground-950">
                {dragActive ? "Drop files to upload" : "Drag & drop files here"}
              </p>
              <p className="text-sm text-foreground-600 mt-1">
                Images, video and documents up to 25&nbsp;MB each. File names must be lowercase (letters, numbers, dots, dashes).
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Select label="Folder" value={uploadFolder} onChange={setUploadFolder} options={folderOptions} />
              <button type="button" onClick={() => inputRef.current?.click()} className={btnPrimary}>
                <i className="ri-folder-add-line"></i> Choose files
              </button>
              <input
                ref={inputRef}
                type="file"
                multiple
                className="sr-only"
                onChange={(event) => { handleFiles(event.target.files); event.target.value = ""; }}
              />
            </div>
          </div>

          {uploads.length > 0 ? (
            <ul className="mt-5 space-y-2">
              {uploads.map((item) => (
                <li key={item.id} className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-md px-3 py-2.5">
                  <i className={
                    item.status === "error"
                      ? "ri-error-warning-line text-accent-600"
                      : item.status === "done"
                        ? "ri-checkbox-circle-line text-primary-600"
                        : "ri-loader-4-line animate-spin text-primary-600"
                  }></i>
                  <span className="text-sm text-foreground-800 flex-1 min-w-0 truncate">{item.name}</span>
                  {item.message ? (
                    <span className="text-xs text-accent-700 max-w-[45%] truncate" title={item.message}>{item.message}</span>
                  ) : (
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      {item.status === "done" ? "Uploaded" : "Uploading…"}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <FilterBar>
          <button
            type="button"
            onClick={toggleAll}
            disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-700 whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            <i className={`${allSelected ? "ri-checkbox-line text-primary-600" : "ri-checkbox-blank-line"} text-lg`}></i>
            {allSelected ? "Clear all" : "Select all"}
          </button>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by file name, title, alt text…" />
          <Select label="Type" value={typeFilter} onChange={setTypeFilter} options={[{ value: "all", label: "All types" }, ...optionsFrom(MEDIA_TYPE)]} />
          <Select label="Folder" value={folderFilter} onChange={setFolderFilter} options={[{ value: "all", label: "All folders" }, ...folders.map((folder) => ({ value: folder, label: folder }))]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} assets</span>
        </FilterBar>

        {selectedIds.length > 0 ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-secondary-50 border border-secondary-200 rounded-lg px-4 py-3">
            <p className="text-sm font-semibold text-foreground-800 flex-1">
              {selectedIds.length} asset{selectedIds.length === 1 ? "" : "s"} selected
            </p>
            {confirmBulk ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-accent-900">Delete {selectedIds.length} asset{selectedIds.length === 1 ? "" : "s"} permanently?</span>
                <button type="button" onClick={bulkDelete} disabled={bulkBusy} className={btnAccent}>
                  {bulkBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-delete-bin-line"></i>} Confirm delete
                </button>
                <button type="button" onClick={() => setConfirmBulk(false)} className={btnGhost}>Cancel</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <button type="button" onClick={() => bulkSetStatus("archived", `${selectedIds.length} assets archived.`)} disabled={bulkBusy} className={btnGhost}>
                  <i className="ri-archive-line"></i> Archive
                </button>
                <button type="button" onClick={() => bulkSetStatus("active", `${selectedIds.length} assets restored.`)} disabled={bulkBusy} className={btnGhost}>
                  <i className="ri-arrow-go-back-line"></i> Restore
                </button>
                <button type="button" onClick={() => setConfirmBulk(true)} disabled={bulkBusy} className={btnGhost}>
                  <i className="ri-delete-bin-line"></i> Delete
                </button>
                <button type="button" onClick={() => setSelectedIds([])} className={btnGhost}>
                  <i className="ri-close-line"></i> Clear
                </button>
              </div>
            )}
          </div>
        ) : null}

        {loading ? (
          <LoadingState label="Loading media…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-image-2-line" title={rows.length === 0 ? "No media yet" : "No media match"} message="Drag files into the uploader above to add them here." />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((asset) => {
              const url = displayUrl(asset.url);
              const isSelected = selectedSet.has(asset.id);
              return (
                <div
                  key={asset.id}
                  className={`group relative bg-background-50 border rounded-lg overflow-hidden transition-colors ${
                    isSelected ? "border-primary-500" : "border-background-200 hover:border-primary-300"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSelect(asset.id)}
                    aria-label={isSelected ? `Deselect ${asset.file_name}` : `Select ${asset.file_name}`}
                    aria-pressed={isSelected}
                    className={`absolute top-2 left-2 z-10 w-7 h-7 flex items-center justify-center rounded-md border transition-colors cursor-pointer ${
                      isSelected
                        ? "bg-primary-500 border-primary-500 text-background-50"
                        : "bg-background-50/90 border-background-300 text-foreground-500 hover:border-primary-400"
                    }`}
                  >
                    <i className={isSelected ? "ri-checkbox-line" : "ri-checkbox-blank-line"}></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedId(asset.id)}
                    className="block w-full text-left cursor-pointer"
                  >
                    <div className="w-full h-32 bg-background-100 flex items-center justify-center text-foreground-400">
                      {asset.asset_type === "image" && url ? (
                        <img src={url} alt={asset.alt_text || asset.file_name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <i className={`${asset.asset_type === "video" ? "ri-film-line" : "ri-file-text-line"} text-3xl`}></i>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-semibold text-foreground-950 truncate">{asset.title || asset.file_name}</p>
                      <div className="flex items-center justify-between gap-2 mt-2">
                        <StatusPill {...metaFor(MEDIA_TYPE, asset.asset_type)} withDot={false} />
                        <span className="text-[11px] text-foreground-500 whitespace-nowrap">{formatSize(asset.size_kb)}</span>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Drawer open={Boolean(selected)} onClose={() => setSelectedId(null)} title={selected?.title || selected?.file_name || "Asset"} subtitle="Media asset" footer={selected ? (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => update(selected.id, { status: selected.status === "active" ? "archived" : "active" }, "Asset status updated.")}
            className={btnGhost}
          >
            <i className="ri-archive-line"></i> {selected.status === "active" ? "Archive" : "Restore"}
          </button>
          <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
            <i className="ri-delete-bin-line"></i> Delete
          </button>
        </div>
      ) : null}>
        {selected ? (
          <>
            {selected.asset_type === "image" && displayUrl(selected.url) ? (
              <div className="w-full h-48 rounded-lg overflow-hidden bg-background-100">
                <img src={displayUrl(selected.url) as string} alt={selected.alt_text || selected.file_name} className="w-full h-full object-cover object-top" />
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(MEDIA_TYPE, selected.asset_type)} />
              <StatusPill {...metaFor(MEDIA_STATUS, selected.status)} />
            </div>

            <DetailList
              items={[
                { icon: "ri-file-line", label: "File name", value: selected.file_name },
                { icon: "ri-folder-line", label: "Folder", value: selected.folder || "Unfiled" },
                { icon: "ri-hard-drive-2-line", label: "Size", value: formatSize(selected.size_kb) },
                { icon: "ri-aspect-ratio-line", label: "Dimensions", value: selected.dimensions || "—" },
                { icon: "ri-user-line", label: "Uploaded by", value: selected.uploaded_by || "—" },
                { icon: "ri-links-line", label: "Used in", value: `${selected.usage_count} place${selected.usage_count === 1 ? "" : "s"}` },
                { icon: "ri-calendar-line", label: "Added", value: formatDate(selected.created_at) },
              ]}
            />

            {selected.alt_text ? (
              <Field label="Alt text">
                <p className="text-sm text-foreground-700">{selected.alt_text}</p>
              </Field>
            ) : null}

            {displayUrl(selected.url) ? (
              <a
                href={displayUrl(selected.url) as string}
                target="_blank"
                rel="noreferrer"
                className={btnGhost}
              >
                <i className="ri-external-link-line"></i> Open file
              </a>
            ) : null}

            <Field label="Status">
              <select
                value={selected.status}
                onChange={(event) => update(selected.id, { status: event.target.value }, "Asset status updated.")}
                className={fieldInput}
              >
                {optionsFrom(MEDIA_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Uploads are stored in Backend Storage and served through secure links.
      </p>
      {node}
    </div>
  );
}